import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Wallet, TrendingUp, Receipt, RotateCcw, Package, RefreshCw, AlertTriangle, Link2, Link2Off,
  Search, ChevronDown, ChevronUp, Info, Coins, CalendarClock, Image as ImageIcon, CheckCircle,
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, Cell, BarChart,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';

// ─── Types ────────────────────────────────────────────────────────────────────
interface FinanceState {
  status: 'idle' | 'running' | 'error';
  progress: string | null;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  payoutsCount: number;
  linesCount: number;
  dashboard?: any;
}
interface Totals {
  sales: number; refunds: number; commissions: number; services: number; other: number; payout: number; fees: number;
  units: number; returnedUnits: number; netUnits: number; orders: number;
  cogs: number; transport: number; storage: number; packaging: number; productCosts: number;
  knownPayout: number; knownSales: number; knownUnits: number;
  profit: number; marginPct: number | null; profitPerUnit: number | null; profitPerOrder: number | null;
  unknownCostSkus: number; unknownCostPayout: number; unknownCostUnits: number; unresolvedPayout: number;
  commissionPct: number | null; servicesPerOrder: number | null; feesPct: number | null; refundPct: number | null;
}
interface SkuRow {
  key: string; sku: string | null; productId: string | null; title: string | null;
  groupName: string | null; groupId: string | null; image: string | null; costSource: string | null;
  sales: number; refunds: number; commissions: number; services: number; other: number; payout: number; fees: number;
  units: number; returnedUnits: number; netUnits: number; orders: number;
  costKnown: boolean; costPerUnit: number | null; productCosts: number;
  profit: number | null; marginPct: number | null; profitPerUnit: number | null;
}
interface MonthRow { month: string; label: string; sales: number; fees: number; refunds: number; payout: number; units: number; orders: number; productCosts: number; profit: number }
interface PayoutPeriod {
  storeId?: string; storeName?: string | null;
  echeance: string; status: string; statusRaw: string | null; executedAt: string | null; externalId: string | null; payoutTotal: number | null;
  sales: number; commissions: number; services: number; refunds: number; other: number; payout: number; units: number; orders: number; productCosts: number; profit: number;
}
interface Profit {
  period: { from: string; to: string; days: number; basis: string };
  totals: Totals; bySku: SkuRow[]; monthly: MonthRow[]; payouts: PayoutPeriod[];
  warnings: { type: string; text: string }[]; finance: FinanceState | null;
}
interface Payout {
  _id: string; storeId?: string; storeName?: string | null; echeance: string; status: string; statusRaw: string | null; externalId: string | null; executedAt: string | null;
  total: number; sales: number; commissions: number; refunds: number; services: number; linesCount: number;
}
interface PayoutList {
  payouts: Payout[];
  summary: {
    count: number; paidCount: number; paidTotal: number; pendingTotal: number;
    pending: { echeance: string; status: string; statusRaw: string | null; total: number }[];
    allTime: { sales: number; commissions: number; services: number; refunds: number; total: number };
    avgPaid: number; lastPaid: Payout | null;
  };
  finance: FinanceState | null;
}
interface Group { _id: string; name: string; imageUrl: string | null; buyingRate: number; costPerUnit: number }
interface Suggestion { groupId: string; name: string; imageUrl: string | null; costPerUnit: number; score: number; confidence: 'High' | 'Medium' | 'Low' }
interface PayoutOrder { orderReference: string; transactionAt: string | null; payout: number; sku: string | null; title: string | null; lines: { kind: string; typeHeader: string; typeDetail: string; ttc: number; allocated: boolean }[] }

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMAD = (v: number | null | undefined) => v == null ? '—' : `${Math.round(v).toLocaleString('en-US')} MAD`;
const fmtSigned = (v: number | null | undefined) => v == null ? '—' : `${v < 0 ? '−' : v > 0 ? '+' : ''}${Math.round(Math.abs(v)).toLocaleString('en-US')}`;
const fmtDate = (iso: string | null | undefined) => iso ? new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' }) : '—';
const fmtDay = (iso: string | null | undefined) => iso ? new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : '—';
const STATUS_LABEL: Record<string, string> = { Paid: 'Paid', Processing: 'Payment in progress', Forecast: 'Forecast', Unknown: '—' };
const STATUS_COLOR: Record<string, string> = { Paid: '#1D9E75', Processing: '#378ADD', Forecast: '#EF9F27', Unknown: '#9CA3AF' };
const KIND_LABEL: Record<string, string> = { sale: 'Sale', refund: 'Refund', commission: 'Commission', service: 'Service fee', other: 'Other' };

type SortKey = 'profit' | 'payout' | 'sales' | 'units' | 'marginPct' | 'fees' | 'refunds';

const MonthTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const d: MonthRow = payload[0].payload;
  return (
    <div className="mj__tooltip">
      <div style={{ color: '#9CA3AF', marginBottom: 2 }}>{label}</div>
      <div>Sales <strong>{fmtMAD(d.sales)}</strong></div>
      <div style={{ color: '#E24B4A' }}>Fees {fmtSigned(d.fees)} · Refunds {fmtSigned(d.refunds)}</div>
      <div>Payout <strong>{fmtMAD(d.payout)}</strong></div>
      <div style={{ color: '#9CA3AF' }}>Product costs −{Math.round(d.productCosts).toLocaleString('en-US')}</div>
      <div style={{ color: d.profit >= 0 ? '#1D9E75' : '#E24B4A' }}>Net profit <strong>{fmtMAD(d.profit)}</strong></div>
    </div>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
interface Props { storeId: string; token: string; days: number; onError: (msg: string | null) => void }

const MarjanFinance: FC<Props> = ({ storeId, token, days, onError }) => {
  const navigate = useNavigate();
  const [profit, setProfit] = useState<Profit | null>(null);
  const [payoutList, setPayoutList] = useState<PayoutList | null>(null);
  const [loading, setLoading] = useState(false);
  const [basis, setBasis] = useState<'transaction' | 'echeance'>('transaction');
  const [finance, setFinance] = useState<FinanceState | null>(null);
  const pollRef = useRef<any>(null);

  const [skuFilter, setSkuFilter] = useState<'all' | 'unlinked' | 'linked'>('all');
  const [skuSearch, setSkuSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('profit');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [showAllSkus, setShowAllSkus] = useState(false);

  const [groups, setGroups] = useState<Group[] | null>(null);
  const [pickerSku, setPickerSku] = useState<string | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [suggestions, setSuggestions] = useState<Record<string, Suggestion[]>>({});
  const [linking, setLinking] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const [expandedPayout, setExpandedPayout] = useState<string | null>(null);
  const [payoutLines, setPayoutLines] = useState<Record<string, PayoutOrder[]>>({});
  const [showAllPayouts, setShowAllPayouts] = useState(false);

  // ── Loading ────────────────────────────────────────────────────────────────
  const load = useCallback(async (silent = false) => {
    if (!storeId) return;
    if (!silent) setLoading(true);
    const [p, l] = await Promise.all([
      dispatch(actions.getMarjanProfit, {}, token, { id: storeId, days, basis }),
      dispatch(actions.getMarjanPayouts, {}, token, { id: storeId }),
    ]);
    if (p?.error) onError(p.error.message);
    else if (p?.totals) { setProfit(p); setFinance(p.finance ?? null); }
    if (l?.payouts) setPayoutList(l);
    if (!silent) setLoading(false);
  }, [storeId, token, days, basis, onError]);

  useEffect(() => { load(); }, [load]);

  // Poll while a finance sync runs
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!storeId || finance?.status !== 'running') return;
    pollRef.current = setInterval(async () => {
      const r = await dispatch(actions.getMarjanFinanceStatus, {}, token, { id: storeId });
      if (r?.finance) {
        setFinance(r.finance);
        if (r.finance.status !== 'running') load(true);
      }
    }, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [storeId, finance?.status, token, load]);

  const triggerSync = async (full = false) => {
    const r = await dispatch(actions.syncMarjanFinance, { full }, token, { id: storeId });
    if (r?.error) { onError(r.error.message); return; }
    setFinance(prev => ({ ...(prev || {} as any), status: 'running', progress: 'starting…' }));
  };

  // ── Linking ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!pickerSku) return;
    const onDoc = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerSku(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerSku]);

  const openPicker = async (row: SkuRow) => {
    if (!row.sku) return;
    setPickerSku(row.sku);
    setPickerSearch('');
    if (!groups) {
      const g = await dispatch(actions.getMarjanGroups, {}, token);
      if (Array.isArray(g)) setGroups(g);
    }
    if (!suggestions[row.sku] && row.title) {
      const s = await dispatch(actions.getMarjanLinkSuggestions, {}, token, { title: row.title });
      if (Array.isArray(s)) setSuggestions(prev => ({ ...prev, [row.sku as string]: s }));
    }
  };

  const linkSku = async (row: SkuRow, groupId: string, source: 'manual' | 'suggestion') => {
    if (!row.sku) return;
    setLinking(true);
    const r = await dispatch(actions.setMarjanLink, { groupId, productId: row.productId, title: row.title, source }, token, { sku: row.sku });
    setLinking(false);
    if (r?.error) { onError(r.error.message); return; }
    setPickerSku(null);
    load(true);
  };

  const unlinkSku = async (row: SkuRow) => {
    if (!row.sku) return;
    setLinking(true);
    const r = await dispatch(actions.removeMarjanLink, {}, token, { sku: row.sku });
    setLinking(false);
    if (r?.error) { onError(r.error.message); return; }
    setPickerSku(null);
    load(true);
  };

  // ── Payout lines ───────────────────────────────────────────────────────────
  const payoutKey = (p: { storeId?: string; echeance: string }) => `${p.storeId || storeId}|${p.echeance}`;
  const togglePayout = async (p: Payout) => {
    const key = payoutKey(p);
    if (expandedPayout === key) { setExpandedPayout(null); return; }
    setExpandedPayout(key);
    if (!payoutLines[key]) {
      const r = await dispatch(actions.getMarjanPayoutLines, {}, token, { id: storeId, store: p.storeId, echeance: p.echeance.slice(0, 10) });
      if (r?.orders) setPayoutLines(prev => ({ ...prev, [key]: r.orders }));
    }
  };
  const isAll = storeId === 'all';

  // ── Derived ────────────────────────────────────────────────────────────────
  const t = profit?.totals;
  const skuRows = useMemo(() => {
    if (!profit) return [];
    let rows = profit.bySku;
    if (skuFilter === 'unlinked') rows = rows.filter(r => !r.costKnown);
    if (skuFilter === 'linked') rows = rows.filter(r => r.costKnown);
    if (skuSearch.trim()) {
      const q = skuSearch.toLowerCase();
      rows = rows.filter(r => (r.title || '').toLowerCase().includes(q) || (r.sku || '').toLowerCase().includes(q) || (r.groupName || '').toLowerCase().includes(q));
    }
    const dir = sortDir === 'desc' ? -1 : 1;
    rows = [...rows].sort((a, b) => {
      const av = (a as any)[sortKey], bv = (b as any)[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
    return rows;
  }, [profit, skuFilter, skuSearch, sortKey, sortDir]);
  const visibleSkus = showAllSkus ? skuRows : skuRows.slice(0, 25);

  const setSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(k); setSortDir('desc'); }
  };
  const SortTh: FC<{ k: SortKey; label: string; title?: string }> = ({ k, label, title }) => (
    <th className="num sortable" onClick={() => setSort(k)} title={title}>
      {label}{sortKey === k ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  );

  const isSyncing = finance?.status === 'running';
  const pendingTotal = payoutList?.summary.pendingTotal ?? 0;
  const nextForecast = payoutList?.summary.pending.find(p => p.status === 'Forecast') || null;
  const processing = payoutList?.summary.pending.find(p => p.status === 'Processing') || null;
  const payoutsVisible = showAllPayouts ? (payoutList?.payouts || []) : (payoutList?.payouts || []).slice(0, 8);
  const unlinkedCount = t?.unknownCostSkus ?? 0;
  const profitNeg = (t?.profit ?? 0) < 0;
  const coveragePct = t && t.payout > 0 ? Math.round((t.knownPayout / t.payout) * 100) : 0;

  const unknownWarning = profit?.warnings.find(w => w.type === 'unknown-cost');
  const otherWarnings = profit?.warnings.filter(w => w.type !== 'unknown-cost') || [];

  return (
    <>
      {/* ── Sync banner ─────────────────────────────────────────────────── */}
      {finance && (finance.status === 'running' || finance.status === 'error') && (
        <div className={`mj__sync-banner ${finance.status === 'error' ? 'mj__sync-banner--error' : ''}`}>
          {finance.status === 'running' ? (
            <>
              <RefreshCw size={13} className="mj__refresh-spin" />
              <span><strong>Syncing payouts from Marjane</strong>{finance.progress ? ` — ${finance.progress}` : '…'}</span>
            </>
          ) : (
            <>
              <AlertTriangle size={13} />
              <span><strong>Last payout sync failed:</strong> {finance.lastError}</span>
              <button className="mj__link-btn" onClick={() => triggerSync(false)}>Retry</button>
            </>
          )}
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="mjf__toolbar">
        <div className="mjf__seg" title="Group movements by the day the sale was booked, or by the payout period they were paid in">
          <button className={basis === 'transaction' ? 'active' : ''} onClick={() => setBasis('transaction')}>By sale date</button>
          <button className={basis === 'echeance' ? 'active' : ''} onClick={() => setBasis('echeance')}>By payout date</button>
        </div>
        <button className="mj__refresh-btn" disabled={loading || isSyncing} onClick={() => triggerSync(false)}
          title="Pull the latest payouts, commissions and fees from the Marjane Seller Zone">
          <RefreshCw size={13} className={loading || isSyncing ? 'mj__refresh-spin' : ''} />
          {isSyncing ? 'Syncing…' : 'Sync payouts'}
        </button>
        {finance?.lastSuccessAt && !isSyncing && (
          <span className="mj__last-updated">
            Payouts synced {new Date(finance.lastSuccessAt).toLocaleString()} · {finance.payoutsCount} payouts · {finance.linesCount?.toLocaleString()} movements
          </span>
        )}
      </div>

      {/* ── Hero KPIs ───────────────────────────────────────────────────── */}
      <div className="mjf__hero">
        <div className={`mjf__profit-card ${profitNeg ? 'mjf__profit-card--neg' : ''}`}>
          <div className="mjf__profit-label"><Wallet size={13} /> Marjan net profit · last {days} days</div>
          <div className="mjf__profit-value">{t && t.knownPayout > 0 ? fmtMAD(t.profit) : '—'}</div>
          <div className="mjf__profit-sub">
            {t && t.knownPayout > 0 ? (
              <>
                {t.marginPct != null && <span><strong>{t.marginPct}%</strong> margin</span>}
                {t.profitPerUnit != null && <span><strong>{fmtMAD(t.profitPerUnit)}</strong> / unit</span>}
                {t.profitPerOrder != null && <span><strong>{fmtMAD(t.profitPerOrder)}</strong> / order</span>}
                <span title="Share of the payout whose product cost is known">{coveragePct}% of payout costed</span>
              </>
            ) : (
              <span>{t ? 'No product linked to a cost yet — link products below to compute profit' : ''}</span>
            )}
          </div>
        </div>

        <div className="mj__kpi-card">
          <div className="mj__kpi-label"><Coins size={12} color="#1D9E75" /> Payout (net received)</div>
          <div className="mj__kpi-value">{t ? fmtMAD(t.payout) : '—'}</div>
          <div className="mjf__kpi-breakdown">
            <span>Sales <b>{t ? fmtMAD(t.sales) : '—'}</b></span>
            <span>Product costs <b className="mjf__neg">{t ? `−${Math.round(t.productCosts).toLocaleString('en-US')}` : '—'}</b></span>
          </div>
        </div>

        <div className="mj__kpi-card">
          <div className="mj__kpi-label"><Receipt size={12} color="#E24B4A" /> Marjane fees</div>
          <div className="mj__kpi-value mjf__neg">{t ? fmtSigned(t.fees) : '—'}</div>
          <div className="mjf__kpi-breakdown">
            <span>Commission <b>{t ? `${fmtSigned(t.commissions)}${t.commissionPct != null ? ` (${t.commissionPct}%)` : ''}` : '—'}</b></span>
            <span>Service fees <b>{t ? `${fmtSigned(t.services)}${t.servicesPerOrder != null ? ` (${t.servicesPerOrder}/order)` : ''}` : '—'}</b></span>
            {t && t.other !== 0 && <span>Other / gestures <b>{fmtSigned(t.other)}</b></span>}
          </div>
        </div>

        <div className="mj__kpi-card">
          <div className="mj__kpi-label"><RotateCcw size={12} color="#EF9F27" /> Refunds</div>
          <div className="mj__kpi-value mjf__neg">{t ? fmtSigned(t.refunds) : '—'}</div>
          <div className="mjf__kpi-breakdown">
            <span>Returned units <b>{t ? t.returnedUnits : '—'}</b></span>
            <span>Of sales <b>{t && t.refundPct != null ? `${t.refundPct}%` : '—'}</b></span>
          </div>
        </div>

        <div className="mj__kpi-card">
          <div className="mj__kpi-label"><CalendarClock size={12} color="#378ADD" /> Coming transfers</div>
          <div className="mj__kpi-value">{payoutList ? fmtMAD(pendingTotal) : '—'}</div>
          <div className="mjf__kpi-breakdown">
            {processing && <span>In progress ({fmtDay(processing.echeance)}) <b>{fmtMAD(processing.total)}</b></span>}
            {nextForecast && <span>Forecast ({fmtDay(nextForecast.echeance)}) <b>{fmtMAD(nextForecast.total)}</b></span>}
            {payoutList && !processing && !nextForecast && <span>Nothing pending</span>}
            {payoutList?.summary.lastPaid && <span>Last paid ({fmtDay(payoutList.summary.lastPaid.executedAt || payoutList.summary.lastPaid.echeance)}) <b>{fmtMAD(payoutList.summary.lastPaid.total)}</b></span>}
          </div>
        </div>
      </div>

      {/* ── Warnings ────────────────────────────────────────────────────── */}
      {unknownWarning && (
        <div className="mjf__warning">
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>{unlinkedCount} product{unlinkedCount > 1 ? 's' : ''} without a buying cost</strong> ({fmtMAD(t?.unknownCostPayout)} of payout).
            Marjan SKUs are different from Jumia SKUs, so link each Marjan product to its product group once — the cost then applies to every sale, past and future.
          </span>
          <button className="mj__link-btn" onClick={() => navigate('/marjan/sku-association')}>
            <Link2 size={12} /> Open Marjan SKU Association
          </button>
        </div>
      )}
      {otherWarnings.map(w => (
        <div key={w.type} className={`mjf__warning ${w.type === 'no-data' ? 'mjf__warning--info' : ''}`}>
          <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} /><span>{w.text}</span>
        </div>
      ))}

      {/* ── Charts ──────────────────────────────────────────────────────── */}
      {profit && profit.monthly.length > 0 && (
        <div className="mj__charts-row">
          <div className="mj__panel">
            <div className="mj__panel-header">
              <TrendingUp size={13} color="#1D9E75" />
              <span className="mj__panel-title">Sales, payout &amp; net profit per month</span>
              <span className="mj__panel-count">{basis === 'transaction' ? 'by sale date' : 'by payout date'}</span>
            </div>
            <div className="mj__panel-body" style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={profit.monthly} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: '#9CA3AF' }} axisLine={false} tickLine={false}
                    tickFormatter={(v: number) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)} />
                  <Tooltip content={<MonthTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <ReferenceLine y={0} stroke="#E2E4E9" />
                  <Bar dataKey="sales" name="Sales" fill="#CBD5E1" radius={[4, 4, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="payout" name="Payout" fill="#378ADD" radius={[4, 4, 0, 0]} maxBarSize={28} />
                  <Bar dataKey="profit" name="Net profit" radius={[4, 4, 0, 0]} maxBarSize={28}>
                    {profit.monthly.map((m, i) => <Cell key={i} fill={m.profit >= 0 ? '#1D9E75' : '#E24B4A'} />)}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {payoutList && payoutList.payouts.length > 0 && (
            <div className="mj__panel">
              <div className="mj__panel-header">
                <Coins size={13} color="#378ADD" />
                <span className="mj__panel-title">Transfers from Marjane</span>
                <span className="mj__panel-count">every 10 days · {fmtMAD(payoutList.summary.avgPaid)} avg</span>
              </div>
              <div className="mj__panel-body" style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[...payoutList.payouts].reverse().slice(-18)} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                    <XAxis dataKey="echeance" tick={{ fontSize: 9, fill: '#9CA3AF' }} tickFormatter={(v: string) => fmtDate(v).replace(/ \d\d$/, '')}
                      axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={20} />
                    <YAxis tick={{ fontSize: 9, fill: '#9CA3AF' }} axisLine={false} tickLine={false}
                      tickFormatter={(v: number) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)} />
                    <Tooltip formatter={(v: any) => fmtMAD(v)} labelFormatter={(l: any) => `Échéance ${fmtDate(l)}`} contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="total" name="Net transfer" radius={[4, 4, 0, 0]} maxBarSize={26}>
                      {[...payoutList.payouts].reverse().slice(-18).map((p, i) => <Cell key={i} fill={STATUS_COLOR[p.status] || '#9CA3AF'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Per-SKU profit ──────────────────────────────────────────────── */}
      <div className="mj__panel" id="mjf-sku-table">
        <div className="mj__panel-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
          <Package size={13} color="#1D9E75" />
          <span className="mj__panel-title">Profit per product</span>
          <span className="mj__panel-count">{profit ? `${profit.bySku.length} SKUs · last ${days} days` : ''}</span>
          <div className="mjf__filter-chips">
            <div style={{ position: 'relative' }}>
              <Search size={12} style={{ position: 'absolute', left: 8, top: 8, color: '#9CA3AF' }} />
              <input className="mjf__search" style={{ paddingLeft: 24 }} placeholder="Search product / SKU"
                value={skuSearch} onChange={e => setSkuSearch(e.target.value)} />
            </div>
            <button className={`mjf__chip ${skuFilter === 'all' ? 'active' : ''}`} onClick={() => setSkuFilter('all')}>All</button>
            <button className={`mjf__chip ${skuFilter === 'unlinked' ? 'active' : ''}`} onClick={() => setSkuFilter('unlinked')}>
              Needs cost link{unlinkedCount ? ` · ${unlinkedCount}` : ''}
            </button>
            <button className={`mjf__chip ${skuFilter === 'linked' ? 'active' : ''}`} onClick={() => setSkuFilter('linked')}>Costed</button>
          </div>
        </div>
        <div className="mj__table-scroll">
          {!profit || skuRows.length === 0 ? (
            <div className="mj__empty-note">{loading ? 'Loading…' : 'No products in this range.'}</div>
          ) : (
            <table className="mj__table mjf__table">
              <thead>
                <tr>
                  <th>Product</th>
                  <SortTh k="units" label="Units" title="Units sold (returned in red)" />
                  <SortTh k="sales" label="Sales" />
                  <SortTh k="fees" label="Fees" title="Commission + service fees (+ other gestures)" />
                  <SortTh k="refunds" label="Refunds" />
                  <SortTh k="payout" label="Payout" title="What Marjane actually pays for this product" />
                  <th className="num">Cost / unit</th>
                  <th className="num">Product costs</th>
                  <SortTh k="profit" label="Net profit" />
                  <SortTh k="marginPct" label="Margin" />
                  <th className="num">Profit / unit</th>
                </tr>
              </thead>
              <tbody>
                {visibleSkus.map(r => {
                  const pickerOpen = pickerSku === r.sku && !!r.sku;
                  const sugg = r.sku ? suggestions[r.sku] : undefined;
                  const q = pickerSearch.trim().toLowerCase();
                  const filteredGroups = (groups || []).filter(g => !q || g.name.toLowerCase().includes(q)).slice(0, 40);
                  return (
                    <tr key={r.key} className={!r.costKnown ? 'mjf__row--unknown' : ''}>
                      <td>
                        <div className="mjf__prod">
                          {r.image ? <img className="mjf__prod-img" src={r.image} alt="" />
                            : <div className="mjf__prod-img mjf__prod-img--empty"><ImageIcon size={14} /></div>}
                          <div style={{ minWidth: 0 }}>
                            <div className="mjf__prod-title" title={r.title || ''}>{r.title || r.sku || r.productId || 'Unmatched movements'}</div>
                            <div className="mj__cell-sku mjf__mono">{r.sku || (r.productId ? `Marjane ${r.productId}` : 'no order line matched')}</div>
                            {r.sku && (
                              <div className="mjf__picker-wrap" ref={pickerOpen ? pickerRef : undefined} style={{ marginTop: 3 }}>
                                <button className={`mjf__link-btn ${r.costKnown ? 'mjf__link-btn--linked' : ''}`} onClick={() => openPicker(r)}
                                  title={r.costKnown ? `Cost from product group "${r.groupName}"${r.costSource === 'jumia-sku' ? ' (same SKU as Jumia)' : ''} — click to change` : 'Link to a product group to get the buying cost'}>
                                  {r.costKnown ? <><Link2 size={11} /> {r.groupName}</> : <><Link2Off size={11} /> Link cost</>}
                                  <ChevronDown size={10} />
                                </button>
                                {pickerOpen && (
                                  <div className="mjf__picker">
                                    <input autoFocus placeholder="Search product groups…" value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} />
                                    {!q && sugg && sugg.length > 0 && (
                                      <>
                                        <div className="mjf__picker-section">Suggested (by name)</div>
                                        {sugg.map(s => (
                                          <button key={s.groupId} className="mjf__picker-item" disabled={linking} onClick={() => linkSku(r, s.groupId, 'suggestion')}>
                                            {s.imageUrl ? <img src={s.imageUrl} alt="" /> : <div className="mjf__prod-img--empty"><ImageIcon size={12} /></div>}
                                            <span className="mjf__picker-name" title={s.name}>{s.name}</span>
                                            <span className={`mjf__conf mjf__conf--${s.confidence}`}>{s.confidence}</span>
                                            <span className="mjf__picker-cost">{fmtMAD(s.costPerUnit)}</span>
                                          </button>
                                        ))}
                                      </>
                                    )}
                                    <div className="mjf__picker-section">{q ? 'Matching groups' : 'All product groups'}</div>
                                    {!groups && <div className="mj__empty-note">Loading groups…</div>}
                                    {groups && filteredGroups.length === 0 && <div className="mj__empty-note">No group matches</div>}
                                    {filteredGroups.map(g => (
                                      <button key={g._id} className="mjf__picker-item" disabled={linking} onClick={() => linkSku(r, g._id, 'manual')}>
                                        {g.imageUrl ? <img src={g.imageUrl} alt="" /> : <div className="mjf__prod-img--empty"><ImageIcon size={12} /></div>}
                                        <span className="mjf__picker-name" title={g.name}>{g.name}</span>
                                        <span className="mjf__picker-cost">{fmtMAD(g.costPerUnit)}</span>
                                      </button>
                                    ))}
                                    {r.costKnown && r.costSource !== 'jumia-sku' && (
                                      <>
                                        <div className="mjf__picker-section">Current link</div>
                                        <button className="mjf__picker-item mjf__picker-unlink" disabled={linking} onClick={() => unlinkSku(r)}>
                                          <Link2Off size={12} /> Remove link to "{r.groupName}"
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="num">
                        {r.units}{r.returnedUnits > 0 && <span className="mjf__neg" title="returned"> −{r.returnedUnits}</span>}
                      </td>
                      <td className="num">{fmtMAD(r.sales)}</td>
                      <td className="num mjf__neg">{fmtSigned(r.fees)}</td>
                      <td className="num">{r.refunds ? <span className="mjf__neg">{fmtSigned(r.refunds)}</span> : <span className="mjf__muted">—</span>}</td>
                      <td className="num"><strong>{fmtMAD(r.payout)}</strong></td>
                      <td className="num">{r.costKnown ? fmtMAD(r.costPerUnit) : <span className="mjf__muted">?</span>}</td>
                      <td className="num">{r.costKnown ? `−${Math.round(r.productCosts).toLocaleString('en-US')}` : <span className="mjf__muted">?</span>}</td>
                      <td className="num">
                        {r.profit == null ? <span className="mjf__muted" title="Link a product group to compute">unknown</span>
                          : <strong className={r.profit >= 0 ? 'mjf__pos' : 'mjf__neg'}>{fmtMAD(r.profit)}</strong>}
                      </td>
                      <td className="num">{r.marginPct == null ? <span className="mjf__muted">—</span> : `${r.marginPct}%`}</td>
                      <td className="num">{r.profitPerUnit == null ? <span className="mjf__muted">—</span> : fmtMAD(r.profitPerUnit)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {skuRows.length > 25 && (
          <div style={{ padding: '0.5rem 1rem', borderTop: '0.5px solid #F0F1F5' }}>
            <button className="mj__link-btn" onClick={() => setShowAllSkus(v => !v)}>
              {showAllSkus ? <><ChevronUp size={12} /> Show top 25</> : <><ChevronDown size={12} /> Show all {skuRows.length} products</>}
            </button>
          </div>
        )}
      </div>

      {/* ── Payout history ──────────────────────────────────────────────── */}
      {payoutList && (
        <div className="mj__panel" style={{ marginTop: '0.75rem' }}>
          <div className="mj__panel-header">
            <Coins size={13} color="#378ADD" />
            <span className="mj__panel-title">Payout history</span>
            <span className="mj__panel-count">
              {payoutList.summary.paidCount} paid · {fmtMAD(payoutList.summary.paidTotal)} received all-time
            </span>
          </div>
          <div className="mj__table-scroll">
            <table className="mj__table">
              <thead>
                <tr>
                  <th>Échéance</th>
                  {isAll && <th>Store</th>}
                  <th>Status</th>
                  <th className="num">Sales</th>
                  <th className="num">Commission</th>
                  <th className="num">Services</th>
                  <th className="num">Refunds</th>
                  <th className="num">Net transfer</th>
                  <th>Paid on</th>
                  <th>Transfer ref</th>
                  <th className="num">Lines</th>
                </tr>
              </thead>
              <tbody>
                {payoutsVisible.map(p => {
                  const open = expandedPayout === payoutKey(p);
                  const orders = payoutLines[payoutKey(p)];
                  const period = profit?.payouts.find(x => x.echeance === p.echeance && (!isAll || String(x.storeId) === String(p.storeId)));
                  return (
                    <>
                      <tr key={p._id} className="mj__row-click" onClick={() => togglePayout(p)}>
                        <td>{fmtDate(p.echeance)}</td>
                        {isAll && <td><span className="mj__badge mj__badge--gray">{p.storeName || '—'}</span></td>}
                        <td>
                          <span className={`mjf__status mjf__status--${p.status}`}>
                            {p.status === 'Paid' && <CheckCircle size={10} />}{STATUS_LABEL[p.status] || p.statusRaw}
                          </span>
                        </td>
                        <td className="num">{fmtMAD(p.sales)}</td>
                        <td className="num mjf__neg">{fmtSigned(p.commissions)}</td>
                        <td className="num mjf__neg">{fmtSigned(p.services)}</td>
                        <td className="num">{p.refunds ? <span className="mjf__neg">{fmtSigned(p.refunds)}</span> : <span className="mjf__muted">—</span>}</td>
                        <td className="num"><strong>{fmtMAD(p.total)}</strong></td>
                        <td>{fmtDate(p.executedAt)}</td>
                        <td className="mjf__mono">{p.externalId || <span className="mjf__muted">—</span>}</td>
                        <td className="num">{p.linesCount}</td>
                      </tr>
                      {open && (
                        <tr key={`${p._id}-lines`} className="mj__row-detail">
                          <td colSpan={isAll ? 11 : 10}>
                            {!orders ? <div className="mj__empty-note">Loading movements…</div> : (
                              <div className="mjf__lines">
                                {period && basis === 'echeance' && (
                                  <div className="mjf__warning mjf__warning--info" style={{ marginBottom: 4 }}>
                                    <Info size={13} /><span>This payout: product costs {fmtMAD(period.productCosts)} → net profit <strong>{fmtMAD(period.profit)}</strong> ({period.units} units, {period.orders} orders)</span>
                                  </div>
                                )}
                                {orders.map(o => (
                                  <div key={o.orderReference} className="mjf__order-line">
                                    <div>
                                      <div className="mjf__mono">{o.orderReference}</div>
                                      <div className="mjf__muted" style={{ fontSize: '0.68rem' }}>{fmtDate(o.transactionAt)}</div>
                                    </div>
                                    <div>
                                      <div className="mjf__prod-title" title={o.title || ''}>{o.title || <span className="mjf__muted">order not in history</span>}</div>
                                      <div className="mjf__order-mvts">
                                        {o.lines.map((l, i) => (
                                          <span key={i} className="mjf__mvt" title={`${l.typeHeader} — ${l.typeDetail}${l.allocated ? ' (order fee split per product)' : ''}`}>
                                            {KIND_LABEL[l.kind] || l.kind} <b className={l.ttc < 0 ? 'mjf__neg' : 'mjf__pos'}>{fmtSigned(l.ttc)}</b>
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                    <div className="num"><strong>{fmtMAD(o.payout)}</strong></div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(payoutList.payouts.length > 8) && (
            <div style={{ padding: '0.5rem 1rem', borderTop: '0.5px solid #F0F1F5' }}>
              <button className="mj__link-btn" onClick={() => setShowAllPayouts(v => !v)}>
                {showAllPayouts ? <><ChevronUp size={12} /> Show recent</> : <><ChevronDown size={12} /> Show all {payoutList.payouts.length} payouts</>}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MarjanFinance;
